<template>
  <footer id="newsletter-footer" class="bg-white text-black py-12">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex flex-col xl:flex-row gap-[36px] mb-8">
        <div class="flex items-start flex-shrink-0">
          <a href="/" class="inline-block">
            <img src="/logos/Stacked Logo/Black Logo Stacked Cropped.png" alt="Barry & Bonna Publishing"
              class="h-[107px] w-auto flex-shrink-0" />
          </a>
        </div>

        <div class="flex flex-col xl:flex-row xl:flex-wrap xl:items-stretch gap-[36px] xl:gap-[36px] min-w-0 flex-1">
          <div
            class="flex-shrink-0 border-b xl:border-b-0 xl:border-r border-gray-300 pb-6 xl:pb-0 xl:pr-[36px] flex flex-col">
            <h4 class="font-header font-semibold mb-2 text-black capitalize">Who We Are</h4>
            <ul class="list-none space-y-2 text-gray-700 text-sm font-body pl-0 m-0 break-words">
              <li class="pl-0 m-0">
                <a href="/about" class="hover:text-primary transition-colors capitalize">About Us</a>
              </li>
              <li class="pl-0 m-0">
                <a href="/team" class="hover:text-primary transition-colors capitalize">Our Team</a>
              </li>
            </ul>
          </div>

          <div
            class="flex-shrink-0 border-b xl:border-b-0 xl:border-r border-gray-300 pb-6 xl:pb-0 xl:pr-[36px] flex flex-col">
            <h4 class="font-header font-semibold mb-2 text-black capitalize">Explore</h4>
            <ul class="list-none space-y-2 text-gray-700 text-sm font-body pl-0 m-0 break-words">
              <li class="pl-0 m-0">
                <a href="/partners" class="hover:text-primary transition-colors capitalize">Partners</a>
              </li>
              <li class="pl-0 m-0">
                <a href="/press" class="hover:text-primary transition-colors capitalize">Press</a>
              </li>
              <li class="pl-0 m-0">
                <a href="/products" class="hover:text-primary transition-colors capitalize">Products and Services</a>
              </li>
            </ul>
          </div>

          <div
            class="flex-shrink-0 border-b xl:border-b-0 xl:border-r border-gray-300 pb-6 xl:pb-0 xl:pr-[36px] flex flex-col">
            <h4 class="font-header font-semibold mb-2 text-black capitalize">Help</h4>
            <ul class="list-none space-y-2 text-gray-700 text-sm font-body pl-0 m-0 break-words">
              <li class="pl-0 m-0">
                <a href="/faq" class="hover:text-primary transition-colors uppercase">FAQ</a>
              </li>
              <li class="pl-0 m-0">
                <a href="/guidelines" class="hover:text-primary transition-colors capitalize">Our Guidelines</a>
              </li>
            </ul>
          </div>

          <div
            class="flex-shrink-0 border-b xl:border-b-0 xl:border-r border-gray-300 pb-6 xl:pb-0 xl:pr-[36px] flex flex-col">
            <h4 class="font-header font-semibold mb-2 text-black capitalize">Terms & Policies</h4>
            <ul class="list-none space-y-2 text-gray-700 text-sm font-body pl-0 m-0 break-words">
              <li class="pl-0 m-0">
                <a href="/terms" class="hover:text-primary transition-colors capitalize">Terms of Use</a>
              </li>
              <li class="pl-0 m-0">
                <a href="/privacy" class="hover:text-primary transition-colors capitalize">Privacy Policy</a>
              </li>
              <li class="pl-0 m-0">
                <a href="/cookies" class="hover:text-primary transition-colors capitalize">Cookies</a>
              </li>
            </ul>
          </div>

          <div class="flex-shrink-0 flex flex-col">
            <h4 class="font-header font-semibold mb-2 text-black capitalize">Connect</h4>
            <ul class="list-none space-y-2 text-gray-700 text-sm font-body pl-0 m-0 break-words">
              <li class="pl-0 m-0">
                <a href="/donate" class="hover:text-primary transition-colors capitalize">Donate</a>
              </li>
              <li class="pl-0 m-0">
                <a href="/contact" class="hover:text-primary transition-colors capitalize">Contact Us</a>
              </li>
              <li class="pl-0 m-0">
                <a :href="`mailto:${contactEmail}`" class="hover:text-primary transition-colors break-all">
                  {{ contactEmail }}
                </a>
              </li>
            </ul>
            <div class="flex gap-4 mt-2">
              <a href="https://www.linkedin.com/company/barryandbonna/" target="_blank" rel="noopener noreferrer"
                class="text-gray-700 hover:text-primary transition-colors flex-shrink-0 flex items-center justify-center w-6 h-6">
                <svg class="w-6 h-6 flex-shrink-0 scale-90" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill-rule="evenodd"
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                    clip-rule="evenodd" />
                </svg>
              </a>
              <a href="https://www.instagram.com/barryandbonna/" target="_blank" rel="noopener noreferrer"
                class="text-gray-700 hover:text-primary transition-colors flex-shrink-0 flex items-center justify-center w-6 h-6">
                <svg class="w-6 h-6 flex-shrink-0 scale-110" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill-rule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.06-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.049 1.023-.06 1.351-.06 3.807v.468c0 2.456.011 2.784.06 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.06 4.041.06h.08c2.597 0 2.917-.012 3.96-.06.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.06-1.37.06-4.041v-.08c0-2.597-.012-2.917-.06-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.049-1.351-.06-3.807-.06zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clip-rule="evenodd" />
                </svg>
              </a>
              <a href="https://www.tiktok.com/@barryandbonna?_r=1&_t=ZT-91Y6oCWicJx" target="_blank"
                rel="noopener noreferrer"
                class="text-gray-700 hover:text-primary transition-colors flex-shrink-0 flex items-center justify-center w-6 h-6">
                <svg class="w-6 h-6 flex-shrink-0 scale-105" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="pt-8">
        <div class="text-center text-gray-600 text-sm font-body">
          <p>&copy; {{ currentYear }} <i>Barry & Bonna Publishing</i> LLC. All rights reserved.</p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed, ref } from 'vue'
import { contactService } from '../services/contact'

const currentYear = computed(() => new Date().getFullYear())
const contactEmail = ref('info@barryandbonna.com')

const loadContactEmail = async () => {
  try {
    const result = await contactService.getContactInfo()
    if (result.success && result.data?.fields) {
      const emailField = result.data.fields.find(field => field.type === 'email')
      if (emailField) {
        contactEmail.value = emailField.value
      }
    }
  } catch (error) {
    console.error('Failed to load contact email:', error)
  }
}

loadContactEmail()
</script>

<style scoped></style>
